import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HiOutlineBell, HiOutlineMail } from 'react-icons/hi';
import { markNotificationsRead } from '@/api/content.api';
import { useNotifications } from '@/hooks/api/useContentQueries';

/**
 * Notification bell with unread badge and dropdown, fed by GET /notifications.
 */

export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useNotifications();
  const notifs = data?.notifications ?? [];
  const unread = data?.unread ?? 0;

  // Opening marks all read (toggleNotifs :1244), then the badge clears.
  async function toggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await markNotificationsRead();
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  }

  // Outside-click + Esc close (prototype :1869).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        className="relative grid size-9 place-items-center rounded-lg text-[var(--ink-soft)] transition-colors hover:bg-[var(--paper)] hover:text-[var(--ink)]"
        id="notifBell"
        data-testid="notif-bell"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        onClick={() => void toggle()}
      >
        <HiOutlineBell className="size-5" />
        {unread > 0 ? (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--coral)] px-1 text-[10px] font-bold text-white"
            data-testid="notif-count"
          >
            {unread}
          </span>
        ) : null}
      </button>
      {/* #notifDrop is position:fixed (top-right) per v02.css; toggled by display.
          Contents render only while OPEN — the prototype fills it on open
          (toggleNotifs :1244), and keeping notification bodies out of the DOM
          until then avoids leaking them into any page's HTML. */}
      <div id="notifDrop" data-testid="notif-drop" className={`fixed top-[74px] right-[18px] z-[90] max-h-[65vh] w-[min(360px,92vw)] overflow-y-auto rounded-[14px] border border-[var(--line)] bg-white p-2 shadow-[var(--shadow-lg)] max-[980px]:top-[70px] max-[980px]:right-2 ${open ? 'block' : 'hidden'}`}>
        {open ? (
          notifs.length ? (
            notifs.map((n) => (
              <div className="border-b border-[var(--line)] p-3 text-[13.5px] last:border-b-0" key={n.id}>
                <b className="inline-flex items-center gap-1.5">
                  <HiOutlineMail className="size-4 shrink-0 text-[var(--cobalt)]" aria-hidden />
                  {n.title}
                </b>
                <p className="my-1 text-[var(--ink-soft)]">{n.body}</p>
                <span className="text-[11.5px] text-[var(--ink-soft)]">{new Date(n.createdAt).toLocaleString()}</span>
              </div>
            ))
          ) : (
            <div className="border-b border-[var(--line)] p-3 text-[13.5px] last:border-b-0">
              <p className="my-1 text-[var(--ink-soft)]">No notifications yet. Enrolling, a purchase, or a mentor answering your question will show up here.</p>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
